import posthog from 'posthog-js';
import { getPageContext, getUTMParams } from './posthog-helpers';

/*
 * Client-side PostHog Tracking API
 * Unified interface for tracking events, user identification, and properties
 */

/**
 * Track a custom event with optional properties
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
    if (typeof window === 'undefined') return;

    try {
        posthog.capture(eventName, {
            ...getPageContext(),
            ...properties,
        });
    } catch (error) {
        console.error('[PostHog] Error tracking event:', error);
    }
};

/**
 * Track a page view (manual)
 */
export const trackPageView = (url?: string) => {
    if (typeof window === 'undefined') return;

    try {
        const pageUrl = url || window.location.href;
        posthog.capture('$pageview', {
            ...getPageContext(),
            $current_url: pageUrl,
        });
    } catch (error) {
        console.error('[PostHog] Error tracking page view:', error);
    }
};

/**
 * Identify a user with their ID and optional properties
 */
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
    if (typeof window === 'undefined') return;

    try {
        posthog.identify(userId, properties);
    } catch (error) {
        console.error('[PostHog] Error identifying user:', error);
    }
};

/**
 * Set user properties without changing the identity
 */
export const setUserProperties = (properties: Record<string, any>) => {
    if (typeof window === 'undefined') return;

    try {
        posthog.setPersonProperties(properties);
    } catch (error) {
        console.error('[PostHog] Error setting user properties:', error);
    }
};

/**
 * Set super properties that will be sent with every event
 */
export const setSuperProperties = (properties: Record<string, any>) => {
    if (typeof window === 'undefined') return;

    try {
        posthog.register(properties);
    } catch (error) {
        console.error('[PostHog] Error setting super properties:', error);
    }
};

/**
 * Reset user identity (e.g., on logout)
 */
export const resetUser = () => {
    if (typeof window === 'undefined') return;

    try {
        posthog.reset();
    } catch (error) {
        console.error('[PostHog] Error resetting user:', error);
    }
};

/**
 * Specialized tracking: Form events
 */
export const trackFormEvent = (action: string, formData: Record<string, any>) => {
    trackEvent(`form_${action}`, {
        ...formData,
        ...getPageContext(),
    });
};

/**
 * Specialized tracking: Video events
 */
export const trackVideoEvent = (action: string, videoData: Record<string, any>) => {
    trackEvent(`video_${action}`, {
        ...videoData,
        ...getPageContext(),
    });
};

/**
 * Specialized tracking: Click events
 */
export const trackClick = (elementData: Record<string, any>) => {
    trackEvent('button_clicked', {
        ...elementData,
        ...getPageContext(),
    });
};

/**
 * Specialized tracking: Link clicks
 */
export const trackLinkClick = (linkData: Record<string, any>) => {
    trackEvent('link_clicked', {
        ...linkData,
        ...getPageContext(),
    });
};

/**
 * Specialized tracking: Conversion events (high-value actions)
 */
export const trackConversion = (conversionType: string, data?: Record<string, any>) => {
    trackEvent(conversionType, {
        ...data,
        ...getPageContext(),
        ...getUTMParams(),
        is_conversion: true,
    });
};

/**
 * Session replay controls
 */
export const startRecording = () => {
    if (typeof window === 'undefined') return;

    try {
        posthog.startSessionRecording();
    } catch (error) {
        console.error('[PostHog] Error starting recording:', error);
    }
};

export const stopRecording = () => {
    if (typeof window === 'undefined') return;

    try {
        posthog.stopSessionRecording();
    } catch (error) {
        console.error('[PostHog] Error stopping recording:', error);
    }
};

export const captureReplaySnapshot = (tag: string) => {
    if (typeof window === 'undefined') return;

    try {
        posthog.capture('$snapshot', { snapshot_tag: tag });
    } catch (error) {
        console.error('[PostHog] Error capturing replay snapshot:', error);
    }
};

/**
 * Feature flags (deferred for now, but included for future use)
 */
export const getFeatureFlag = (flagKey: string): string | boolean | undefined => {
    if (typeof window === 'undefined') return undefined;

    try {
        return posthog.getFeatureFlag(flagKey);
    } catch (error) {
        console.error('[PostHog] Error getting feature flag:', error);
        return undefined;
    }
};

export const isFeatureEnabled = (flagKey: string): boolean => {
    if (typeof window === 'undefined') return false;

    try {
        return posthog.isFeatureEnabled(flagKey) || false;
    } catch (error) {
        console.error('[PostHog] Error checking feature flag:', error);
        return false;
    }
};

/**
 * Get PostHog client instance (for advanced use cases)
 */
export const getPostHogClient = () => {
    if (typeof window === 'undefined') return null;
    return posthog;
};
